/**
 * Cross-cutting guard so the global stale-chunk auto-reload (see main.tsx) never
 * reloads the page out from under in-progress work.
 *
 * A long-running operation (e.g. a package scan) wraps itself in holdReload()
 * and calls the returned release when done. While any hold is active, the reload
 * handler defers instead of doing a destructive `window.location.reload()` that
 * would discard the user's work for what is usually a transient (a backgrounded
 * tab dropping an in-flight dynamic import). Counter-based, so concurrent ops
 * (a multi-file scan) nest safely; each release is idempotent.
 */
let holds = 0;

export function holdReload(): () => void {
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
  };
}

export function reloadHeld(): boolean {
  return holds > 0;
}

// ── Stale-chunk recovery ──────────────────────────────────────────────────────
// After a deploy, a long-lived tab still references old hashed chunk URLs; the
// next lazy route import 404s. The fix is a one-shot reload to pull the fresh
// index.html + chunks. The guard MUST be time-based, not a sticky one-shot flag:
// the old per-module/single-flag design recovered the FIRST stale chunk but then
// crashed the SECOND one (a later deploy in the same session), which read as
// "leave a page and it fails" sitewide. A timestamp lets every later deploy
// recover while still blocking a true reload loop (a chunk that re-fails within
// the cooldown clearly won't be fixed by reloading again).
const RELOAD_TS_KEY = "cr_chunk_reload_ts";
const RELOAD_COOLDOWN_MS = 12_000;

/**
 * True when a destructive reload must be SUPPRESSED: in-progress work is held
 * (a running scan), the tab is backgrounded, or the device is offline. All three
 * routinely drop an in-flight dynamic-import fetch, and reloading there would
 * discard the user's work for a transient that resolves on its own. Centralized
 * so every reload path (main.tsx, lazyRetry, ErrorBoundary) shares one rule.
 */
export function reloadSuppressed(): boolean {
  if (reloadHeld()) return true;
  try {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return true;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  } catch {
    /* environment without document/navigator */
  }
  return false;
}

/** True for the family of "stale chunk / failed dynamic import" load errors. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("stale chunk") ||
    msg.includes("failed to fetch dynamically")
  );
}

/**
 * Reload once to recover a stale chunk, with a time-based loop guard. Returns
 * true when a reload was triggered (the page is going away, caller should stop);
 * false when we reloaded too recently to try again (caller should surface the
 * error instead of looping). Persisted in sessionStorage so the guard survives
 * the reload it triggers.
 */
export function attemptChunkReload(): boolean {
  if (typeof window === "undefined") return false;
  // Centralized guard: never reload out from under held work (a running scan), a
  // backgrounded tab, or an offline blip. EVERY caller (main.tsx, lazyRetry,
  // ErrorBoundary) routes through here, so the protection is universal, not just
  // in main.tsx. A backgrounded tab dropping a lazy chunk used to reload via
  // lazyRetry/ErrorBoundary and break an in-flight scan; this closes that.
  if (reloadSuppressed()) return false;
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || "0");
    if (now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}
